import { Component, Inject, OnInit } from '@angular/core';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { TranslateService } from '@ngx-translate/core';
import { MimicFolderService } from '@service/mimic-folder.service';
import { MimicFolderViewData, RootItem, RootItemType } from 'icreate/common/models/mimic-folder';
import { Vessel } from 'icreate/common/models/vessel';
import { getValidationErrors, mapErrors, ValidationErrorMap } from 'icreate/common/validators';
import {
    mimicFolderNameMaxLen,
    mimicFolderValidator,
} from 'icreate/common/validators/mimic-folder/mimic-folder-validator';

enum MimicFolderInputName {
    name = 'name',
}

@Component({
    selector: 'app-mimic-folder-dialog',
    templateUrl: './mimic-folder-dialog.component.html',
    styleUrls: ['./mimic-folder-dialog.component.css'],
    standalone: false,
})
export class MimicFolderDialogComponent implements OnInit {
    readonly NAME_INPUT_NAME = MimicFolderInputName.name;

    MIMIC_MAX_LENGTH = mimicFolderNameMaxLen;
    mimicFolderName: string = '';
    initName: string = '';

    isLoading: boolean = false;

    editMode: boolean = false;
    validationErrors: ValidationErrorMap = {};
    hasError: boolean;

    constructor(
        @Inject(MAT_DIALOG_DATA)
        public data: {
            vessel: Vessel;
            mimicFolder: MimicFolderViewData;
            rootList: RootItem[];
        },
        private dialogRef: MatDialogRef<MimicFolderDialogComponent>,
        private mimicFolderService: MimicFolderService,
        private translateService: TranslateService
    ) {}

    ngOnInit() {
        if (this.data?.mimicFolder) {
            this.editMode = true;
            this.mimicFolderName = this.data.mimicFolder.name;
            this.initName = this.data.mimicFolder.name;
        }
        this.dialogHasErrors();
    }

    changeMimicFolderName(event) {
        if (event.name === this.NAME_INPUT_NAME) {
            this.mimicFolderName = event.value;
            this.dialogHasErrors();
        }
    }

    dialogHasErrors() {
        const validationData = {
            name: this.mimicFolderName,
        };
        const errors = getValidationErrors(mimicFolderValidator(), validationData);
        const mappedErrors: { [key: string]: { message: string } } = mapErrors(errors) || {};

        let isRepeated = false;
        if (this.data.rootList && this.data.rootList.length > 0) {
            this.data.rootList.forEach(item => {
                if (item.type !== RootItemType.folder || this.initName === item.folder.name) return;

                if (item.folder?.name === this.mimicFolderName) {
                    isRepeated = true;
                }
            });
        }

        if (isRepeated) {
            mappedErrors['name'] = {
                message: this.translateService.instant('mimic-folder.create.error.title.repeat'),
            };
        }

        this.validationErrors = Object.keys(mappedErrors).length > 0 ? mappedErrors : null;
        if (mappedErrors['name']?.message) {
            this.hasError = true;
            return;
        }

        this.hasError = false;
    }

    disabledCreateButton() {
        return this.hasError;
    }

    close() {
        this.dialogRef.close();
    }

    async save() {
        this.isLoading = true;
        try {
            if (this.editMode) {
                await this.mimicFolderService.update({
                    ...this.data.mimicFolder,
                    name: this.mimicFolderName,
                });
            } else {
                await this.mimicFolderService.create({
                    id: null,
                    name: this.mimicFolderName,
                    vesselId: this.data.vessel.id,
                    index: this.data.rootList.length,
                });
            }
        } catch (error) {
            console.log(error);
        } finally {
            this.isLoading = false;
            this.dialogRef.close();
        }
    }

    public get validatorFunction() {
        return mimicFolderValidator;
    }
}
